import * as THREE from 'three';

import { Axial, axialKey, axialToWorld } from '../map/HexCoordinates';
import { elevationToY } from '../map/HexMetrics';
import { HexCellData, waterLevel } from '../map/HexSurface';
import { lookFor } from './BuildingShapes';

/**
 * What the cursor is carrying.
 *
 * Dokładnie to, co by stanęło — model albo sylwetka — na polu pod kursorem,
 * zielone tam, gdzie stanie, czerwone tam, gdzie nie. Kolorowy heks pod spodem
 * odpowiada na „które pole"; to odpowiada na „co", czyli na pytanie, które lista
 * nazw w panelu zostawia otwarte.
 *
 * **Materiał jest duchowy, nie modelowy, i to jest cały sens tego obiektu.**
 * Model przynosi własne tekstury, ale duch pokazany w nich byłby nie do
 * odróżnienia od budynku, który już stoi — a różnica między „to jest twoje"
 * a „to mogłoby być twoje" jest jedyną rzeczą, którą ten podgląd mówi. Bierzemy
 * więc z modelu **kształt, nie wygląd**.
 */

/** Solid enough to read the shape, thin enough not to be mistaken for one built. */
const OPACITY = 0.5;

export class BuildGhost {
  readonly object = new THREE.Group();

  private readonly material: THREE.MeshBasicMaterial;
  /** Który rodzaj jest teraz zbudowany, żeby nie budować go co klatkę. */
  private showing: string | null = null;
  private at: Axial | null = null;

  constructor(private readonly world: Map<number, HexCellData>) {
    this.material = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: OPACITY,
      // Unlit and never written to the depth buffer: it is interface standing
      // in the scene, so it must not shade like terrain nor hide what it
      // hovers over — including the cell overlay directly beneath it.
      depthWrite: false,
    });

    this.object.visible = false;
    this.object.renderOrder = 3;
    // Its position is rewritten in place, and three builds a bounding sphere
    // once — the same trap `HexHighlight` documents.
    this.object.frustumCulled = false;
  }

  /**
   * Stand it on a cell, in the colour of whether it would be allowed.
   *
   * `turn` obraca ducha tak, jak stanie budynek ze skrzydłami. **Liczy go
   * wołający jedną funkcją z tą, która obraca postawiony budynek** — duch
   * z własnym rachunkiem przestałby być podglądem w dniu, w którym tamten się
   * zmieni.
   */
  showAt(key: string, cell: HexCellData, allowed: boolean, turn = 0): void {
    if (this.showing !== key) {
      this.showing = key;
      // Same geometrie, jeden wspólny materiał: siatki są tu tanie, a duch
      // zmienia rodzaj tylko wtedy, gdy gracz wybierze inny budynek.
      this.object.clear();
      for (const part of lookFor(key).parts) {
        const mesh = new THREE.Mesh(part.geometry, this.material);
        mesh.frustumCulled = false;
        this.object.add(mesh);
      }
    }

    this.material.color.setHex(allowed ? 0x8ff2a6 : 0xff8a8a);
    this.object.rotation.y = turn;

    this.at = { q: cell.q, r: cell.r };
    this.seat();
    this.object.visible = true;
  }

  hide(): void {
    this.object.visible = false;
    this.at = null;
  }

  /** The ground under it may have just streamed in. */
  refresh(): void {
    if (this.at) this.seat();
  }

  dispose(): void {
    this.object.clear();
    this.material.dispose();
  }

  private seat(): void {
    if (!this.at) return;

    const { x, z } = axialToWorld(this.at.q, this.at.r);
    const data = this.world.get(axialKey(this.at.q, this.at.r));
    if (!data) return;

    // On water it stands on the mirror, not on the bed: a cell under a lake
    // carries the bottom of it, and a hut sunk to the bottom of a lake is a
    // worse answer than one floating on it — both are refused anyway, and the
    // refused one still has to be visible where the cursor is.
    const surface = waterLevel(data);
    this.object.position.set(x, elevationToY(surface ?? data.elevation), z);
  }
}
